import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Bike, CheckCircle, XCircle, Clock, Eye, Phone, Mail, KeyRound } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import AdminLayout from '../../components/admin/AdminLayout';
import ResetPasswordModal from '../../components/admin/ResetPasswordModal';

const AdminBikers = () => {
  const [bikers, setBikers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(null);
  const [stats, setStats] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);

  useEffect(() => {
    fetchBikers();
    fetchStats();
  }, [filter]);

  const fetchStats = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get('/admin/bikers/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching biker stats:', error);
    }
  };

  const fetchBikers = async () => {
    try {
      const api = (await import('../../utils/api')).default;
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await api.get(`/admin/bikers${params}`);
      setBikers(response.data.bikers || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bikers:', error);
      toast({
        title: 'Error',
        description: 'Failed to load bikers',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const handleApprove = async (bikerId) => {
    setActionLoading(bikerId);
    try {
      const api = (await import('../../utils/api')).default;
      await api.put(`/admin/bikers/${bikerId}/approval`, { status: 'approved' });
      
      toast({
        title: 'Success',
        description: 'Biker approved successfully',
      });
      
      fetchBikers();
      fetchStats();
    } catch (error) {
      console.error('Error approving biker:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to approve biker',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (bikerId) => {
    const reason = window.prompt('Please provide a reason for rejection:');
    if (!reason) return;

    setActionLoading(bikerId);
    try {
      const api = (await import('../../utils/api')).default;
      await api.put(`/admin/bikers/${bikerId}/approval`, { status: 'rejected', rejectionReason: reason });
      
      toast({
        title: 'Success',
        description: 'Biker application rejected',
      });
      
      fetchBikers();
      fetchStats();
    } catch (error) {
      console.error('Error rejecting biker:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to reject biker',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' },
      approved: { label: 'Approved', className: 'bg-green-100 text-green-800' },
      rejected: { label: 'Rejected', className: 'bg-red-100 text-red-800' },
      suspended: { label: 'Suspended', className: 'bg-orange-100 text-orange-800' },
    };
    return config[status] || config.pending;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Bikers</h1>
            <p className="text-gray-600 mt-1">Manage motorbike drivers on the platform</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Bike className="h-5 w-5 text-orange-600" />
            <span>Total: {bikers.length}</span>
          </div>
        </div>

        {/* Filter Tabs */}
        <Card className="p-4">
          <div className="flex gap-2">
            {['all', 'pending', 'approved', 'rejected'].map((status) => (
              <Button
                key={status}
                variant={filter === status ? 'default' : 'outline'}
                onClick={() => setFilter(status)}
                className={filter === status ? 'bg-orange-600 hover:bg-orange-700' : ''}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
          </div>
        </Card>

        {/* Bikers List */}
        {bikers.length === 0 ? (
          <Card className="p-12 text-center">
            <Bike className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Bikers Found</h3>
            <p className="text-gray-600">
              {filter === 'all' 
                ? 'No motorbike drivers have applied yet.'
                : `No ${filter} bikers at the moment.`}
            </p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {bikers.map((biker) => {
              const statusConfig = getStatusBadge(biker.approvalStatus);
              
              return (
                <Card key={biker.userId} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                        <Bike className="h-6 w-6 text-orange-600" />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{biker.name}</h3>
                          <Badge className={statusConfig.className}>
                            {statusConfig.label}
                          </Badge>
                          {biker.isOnline && (
                            <Badge className="bg-green-100 text-green-800">
                              Online
                            </Badge>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 mt-3">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone className="h-4 w-4" />
                            <span>{biker.phone}</span>
                          </div>
                          {biker.email && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Mail className="h-4 w-4" />
                              <span>{biker.email}</span>
                            </div>
                          )}
                        </div>

                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Vehicle Details</h4>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="text-gray-600">Type:</span>
                              <span className="ml-2 font-medium text-gray-900">
                                {biker.vehicleType || 'Motor Bike'}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">Make/Model:</span>
                              <span className="ml-2 font-medium text-gray-900">
                                {biker.vehicleMake} {biker.vehicleModel}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">Year:</span>
                              <span className="ml-2 font-medium text-gray-900">
                                {biker.vehicleYear}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">Plate:</span>
                              <span className="ml-2 font-medium text-gray-900">
                                {biker.vehiclePlateNumber}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">Color:</span>
                              <span className="ml-2 font-medium text-gray-900">
                                {biker.vehicleColor}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">Wallet:</span>
                              <span className="ml-2 font-medium text-orange-600">
                                K{biker.walletBalance?.toFixed(2) || '0.00'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {biker.approvalStatus === 'pending' && (
                      <div className="flex gap-2 ml-4">
                        <Button
                          onClick={() => handleApprove(biker.userId)}
                          disabled={actionLoading === biker.userId}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleReject(biker.userId)}
                          disabled={actionLoading === biker.userId}
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject
                        </Button>
                        <Button
                          onClick={() => { setResetPasswordUser({ _id: biker.userId, name: biker.name }); setShowResetPassword(true); }}
                          variant="outline"
                          className="text-pink-600 border-pink-300 hover:bg-pink-50"
                          data-testid={`reset-password-biker-${biker.userId}`}
                        >
                          <KeyRound className="h-4 w-4 mr-2" />
                          Reset Password
                        </Button>
                      </div>
                    )}
                    {biker.approvalStatus !== 'pending' && (
                      <div className="flex gap-2 ml-4">
                        <Button
                          onClick={() => { setResetPasswordUser({ _id: biker.userId, name: biker.name }); setShowResetPassword(true); }}
                          variant="outline"
                          size="sm"
                          className="text-pink-600 border-pink-300 hover:bg-pink-50"
                          data-testid={`reset-password-biker-${biker.userId}`}
                        >
                          <KeyRound className="h-4 w-4 mr-2" />
                          Reset Password
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      <ResetPasswordModal
        isOpen={showResetPassword}
        onClose={() => setShowResetPassword(false)}
        user={resetPasswordUser}
        userType="biker"
      />
    </AdminLayout>
  );
};

export default AdminBikers;
